import {
  applyUpdateToGetDashboardResponse,
  buildUpdateDashboardRequest,
  type UpdateDashboardRequestInit,
} from "@/components/dashboards/dashboards.utils";
import { TABLE_PAGE_SIZE_MD } from "@/components/table/page-size.constants";
import {
  createDashboard,
  deleteDashboard,
  exportDashboard,
  getDashboard,
  getContextualDashboard,
  importDashboard,
  listDashboards,
  resetContextualDashboard,
  updateDashboard,
  updateDashboardSharing,
  upsertContextualDashboard,
} from "@/gen_src/proto/chalk/server/v1/dashboard_service-DashboardService_connectquery";
import type { Dashboard } from "@/gen_src/proto/chalk/artifacts/v1/dashboard_pb";
import {
  ContextualDashboardLocation,
  ListDashboardsRequestSchema,
  type GetDashboardResponse,
} from "@/gen_src/proto/chalk/server/v1/dashboard_service_pb";
import type { MessageInitShape } from "@bufbuild/protobuf";
import {
  makeInvalidationPredicateForMutation,
  useGRPCMutation,
} from "@/utils/hooks/grpc/use-grpc-mutation";
import { useGRPCQuery } from "@/utils/hooks/grpc/use-grpc-query";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useDebouncedCallback } from "@/utils/hooks/form/use-debounce";
import { createQueryOptions, useInfiniteQuery, useTransport } from "@connectrpc/connect-query";
import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/query-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

/**
 * Data layer for `chalk.server.v1.DashboardService`. Pages consume these hooks rather than the
 * generated connect-query methods directly so the query/invalidation wiring lives in one place.
 *
 * Create/delete invalidate every query on the service on success (the {@link useGRPCMutation}
 * default); update writes the cache optimistically and re-syncs when its last in-flight write
 * settles. Build request payloads with the helpers in `./dashboards.utils` (notably
 * `buildUpdateDashboardRequest`, which encodes the update-mask semantics the server expects).
 */

/**
 * Read one dashboard. Disabled until `dashboardId` is known (e.g. while the route is hydrating).
 */
export const useDashboard = (
  dashboardId: string | undefined,
  options?: { refetchInterval?: number },
) =>
  useGRPCQuery(getDashboard, dashboardId != null ? { dashboardId } : undefined, {
    enabled: dashboardId != null,
    ...options,
  });

/** A persisted environment override for a product surface; missing means render its code default. */
export const useContextualDashboard = (location: ContextualDashboardLocation) =>
  useGRPCQuery(getContextualDashboard, { location });

/** The first mutation for a location creates its override; later mutations replace its layout. */
export const useUpsertContextualDashboard = () => useGRPCMutation(upsertContextualDashboard);

/** Deletes a location's override so it immediately follows the code-defined default again. */
export const useResetContextualDashboard = () => useGRPCMutation(resetContextualDashboard);

/**
 * List dashboards in the current environment as an infinite query: pages accumulate in
 * `query.data.pages`, and `fetchNextPage()` feeds the previous page's `next_cursor` back in via the
 * request's `cursor` field. Flatten for rendering with `data.pages.flatMap((p) => p.dashboards)`.
 */
export const useDashboards = (input: MessageInitShape<typeof ListDashboardsRequestSchema>) =>
  useInfiniteQuery(
    listDashboards,
    {
      ...input,
      cursor: undefined as unknown as string,
      limit: input.limit ?? TABLE_PAGE_SIZE_MD,
    },
    {
      pageParamKey: "cursor",
      getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
      // Same `refetchOnMount: "always"` that `useGRPCQuery` injects, set inline since there's no
      // infinite-query wrapper: refetch the loaded pages on mount so renames/creates/deletes made on
      // the detail page show up on return (the query client otherwise caches forever — staleTime:
      // Infinity, refetchOnMount: false).
      refetchOnMount: "always",
      placeholderData: keepPreviousData,
    },
  );

export const useCreateDashboard = () => useGRPCMutation(createDashboard);

/**
 * Change a dashboard's visibility and/or its grant list. Grants are replace-if-present: send the
 * whole list, or omit it to change visibility alone. Owner-only, enforced server-side.
 */
export const useUpdateDashboardSharing = () => useGRPCMutation(updateDashboardSharing);

/** What `onMutate` hands to `onError`/`onSettled`; typed locally since the wrapper's Ctx is unknown. */
interface UpdateDashboardMutationContext {
  queryKey: QueryKey;
  previous: GetDashboardResponse | undefined;
  /** This write's position in the start order; compared against the latest to gate rollback. */
  seq: number;
}

/**
 * Tags dashboard updates in the mutation cache so `isMutating` can count them. Client-scoped (the
 * mutation cache outlives this page), unlike a ref, so remounts can't split the count.
 */
const UPDATE_DASHBOARD_MUTATION_KEY = ["DashboardService", "UpdateDashboard"];

/**
 * Update a dashboard with an optimistic cache write: the predicted result lands in the `GetDashboard`
 * cache immediately (so e.g. a settled drag doesn't snap back while the save is in flight), rolls
 * back from a local snapshot when the newest write fails (no network needed — dashboards get used
 * during incidents), and re-syncs with the server once the last in-flight update settles.
 */
export const useUpdateDashboard = () => {
  const queryClient = useQueryClient();
  const transport = useTransport();
  // Monotonic write sequence: a failed write only rolls back if nothing newer has *started* — a
  // pending count can't tell "I'm alone" from "the newer write already finished".
  const seqRef = useRef(0);
  // A mutation still counts as pending while its own callbacks run, so "I'm the last one" is a
  // count of exactly 1 (the TanStack idiom for skipping mid-burst work).
  const isLastPendingUpdate = () =>
    queryClient.isMutating({ mutationKey: UPDATE_DASHBOARD_MUTATION_KEY }) === 1;

  return useGRPCMutation(
    updateDashboard,
    {
      mutationKey: UPDATE_DASHBOARD_MUTATION_KEY,
      onMutate: async (
        request: UpdateDashboardRequestInit,
      ): Promise<UpdateDashboardMutationContext> => {
        const seq = ++seqRef.current;
        // setQueryData is exact-match, so build the key with createQueryOptions — the same
        // function useDashboard's useQuery uses internally — instead of mirroring its recipe.
        const { queryKey } = createQueryOptions(
          getDashboard,
          { dashboardId: String(request.dashboard?.id ?? "") },
          { transport },
        );
        // Stop in-flight refetches so a stale response can't land on top of the optimistic write.
        await queryClient.cancelQueries({ queryKey });
        const previous = queryClient.getQueryData<GetDashboardResponse>(queryKey);
        if (previous?.dashboard != null) {
          queryClient.setQueryData(queryKey, applyUpdateToGetDashboardResponse(previous, request));
        }
        return { queryKey, previous, seq };
      },
      onError: (error, _request, context) => {
        // Providing onError replaces the wrapper's default toast, so surface the failure here.
        toast.error(`Error saving dashboard: ${error.rawMessage}`, {
          style: { wordBreak: "break-all" },
        });
        // Roll back only if this is still the newest write (seq) and nothing else is pending
        // (client-wide) — an older snapshot would clobber a newer write's result. The last settle
        // re-syncs regardless.
        const ctx = context as UpdateDashboardMutationContext | undefined;
        if (ctx?.previous != null && ctx.seq === seqRef.current && isLastPendingUpdate()) {
          queryClient.setQueryData(ctx.queryKey, ctx.previous);
        }
      },
      onSettled: () => {
        // Re-sync from the server once the burst is over. Doing it per-success instead would let an
        // older write's refetch race a newer write's optimistic state and briefly snap the board back.
        if (isLastPendingUpdate()) {
          void queryClient.invalidateQueries({
            predicate: makeInvalidationPredicateForMutation(updateDashboard),
          });
        }
      },
    },
    // Skip the wrapper's per-success invalidation; the last-settle invalidation above owns re-sync.
    false,
  );
};

export const useDeleteDashboard = () => useGRPCMutation(deleteDashboard);

/** How long typing pauses before a title/description edit is persisted. */
const DASHBOARD_METADATA_SAVE_DEBOUNCE_MS = 600;

export interface DashboardMetadataEditor {
  name: string;
  description: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  /** Flush the pending edit (blur commits immediately rather than waiting out the debounce). */
  onNameBlur: () => void;
  onDescriptionBlur: () => void;
  /** Abandon the in-progress title edit (Escape), restoring the saved name. */
  resetName: () => void;
}

/**
 * Click-to-edit title/description state for the dashboard header: the text is edited locally and
 * autosaved after a typing pause (and on blur), the same way a notebook's title and description save
 * — no edit mode, no save/cancel buttons.
 *
 * An empty name is not persistable (the server requires one), so blurring a blank title restores the
 * saved name. A blank description clears it, which the server stores as unset.
 */
export const useDashboardMetadataEditor = (
  dashboard: Dashboard | undefined,
): DashboardMetadataEditor => {
  const updateDashboard = useUpdateDashboard();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Mirrors of the drafts. Escape resets the text and then blurs within the same event, before React
  // has re-rendered, so the blur handler must read the reset value rather than a stale closure.
  const nameRef = useRef("");
  const descriptionRef = useRef("");

  const setNameDraft = useCallback((next: string) => {
    nameRef.current = next;
    setName(next);
  }, []);

  const setDescriptionDraft = useCallback((next: string) => {
    descriptionRef.current = next;
    setDescription(next);
  }, []);

  // Seed the drafts when the dashboard first loads (and again if the page switches dashboards).
  // After that the local text is authoritative: `useUpdateDashboard` writes the cache optimistically,
  // so re-seeding on every render would fight the user's cursor mid-typing.
  const seededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (dashboard == null || seededIdRef.current === dashboard.id) {
      return;
    }
    seededIdRef.current = dashboard.id;
    setNameDraft(dashboard.name);
    setDescriptionDraft(dashboard.description ?? "");
  }, [dashboard, setNameDraft, setDescriptionDraft]);

  // Read the dashboard and the mutation through refs so the save callbacks stay []-stable across the
  // refetches this page does; they are handed to inputs that would otherwise re-render on every one.
  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;
  const updateDashboardRef = useRef(updateDashboard);
  updateDashboardRef.current = updateDashboard;

  const persist = useCallback((args: { name?: string; description?: string | null }) => {
    const current = dashboardRef.current;
    if (current == null) {
      return;
    }
    void updateDashboardRef.current
      .mutateAsync(buildUpdateDashboardRequest({ id: current.id, ...args }))
      .catch(() => {
        // useUpdateDashboard surfaces the error toast and rolls the cache back.
      });
  }, []);

  const saveName = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      // A blank name is rejected server-side; leave the saved one in place until there's real text.
      if (trimmed === "" || trimmed === dashboardRef.current?.name) {
        return;
      }
      persist({ name: trimmed });
    },
    [persist],
  );

  const saveDescription = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      const stored = dashboardRef.current?.description ?? "";
      if (trimmed === stored) {
        return;
      }
      // null clears it; the server stores blank as unset either way.
      persist({ description: trimmed === "" ? null : trimmed });
    },
    [persist],
  );

  const saveNameDebounced = useDebouncedCallback(saveName, DASHBOARD_METADATA_SAVE_DEBOUNCE_MS);
  const saveDescriptionDebounced = useDebouncedCallback(
    saveDescription,
    DASHBOARD_METADATA_SAVE_DEBOUNCE_MS,
  );

  const onNameChange = useCallback(
    (next: string) => {
      setNameDraft(next);
      saveNameDebounced(next);
    },
    [saveNameDebounced, setNameDraft],
  );

  const onDescriptionChange = useCallback(
    (next: string) => {
      setDescriptionDraft(next);
      saveDescriptionDebounced(next);
    },
    [saveDescriptionDebounced, setDescriptionDraft],
  );

  const resetName = useCallback(() => {
    // Any pending debounced save then re-saves the stored name, which saveName drops as a no-op.
    setNameDraft(dashboardRef.current?.name ?? "");
  }, [setNameDraft]);

  const onNameBlur = useCallback(() => {
    const trimmed = nameRef.current.trim();
    // Nothing to save from a blank title: show the saved name again so the header is never empty.
    if (trimmed === "") {
      resetName();
      return;
    }
    setNameDraft(trimmed);
    saveName(trimmed);
  }, [resetName, saveName, setNameDraft]);

  const onDescriptionBlur = useCallback(() => {
    const trimmed = descriptionRef.current.trim();
    setDescriptionDraft(trimmed);
    saveDescription(trimmed);
  }, [saveDescription, setDescriptionDraft]);

  return {
    name,
    description,
    onNameChange,
    onDescriptionChange,
    onNameBlur,
    onDescriptionBlur,
    resetName,
  };
};

/**
 * Fetch a dashboard's portable JSON export. Shaped as a mutation (not a query) because it's an
 * on-click action whose result goes to a file/clipboard, never the cache — so no invalidation.
 */
export const useExportDashboard = () => useGRPCMutation(exportDashboard, undefined, false);

/**
 * Import a dashboard from its JSON export, creating a new dashboard. The default same-service
 * invalidation refreshes the list; the default error toast is replaced by a no-op because the
 * import modal renders validation errors inline (they're long, path-prefixed strings).
 */
export const useImportDashboard = () => useGRPCMutation(importDashboard, { onError: () => {} });

/**
 * Breadcrumb label override for the top rail: `{ dashboardId: <name> }` on the dashboard detail
 * route, `{}` elsewhere (query disabled). The name is "" until the page-shared GetDashboard query
 * resolves, which suppresses the crumb so the raw id never shows.
 */
const NO_BREADCRUMB_LABELS: Readonly<Record<string, string>> = {};

export const useDashboardBreadcrumbLabels = (): Readonly<Record<string, string>> => {
  // dashboardId only exists in the URL on the detail route, so its presence is the route check.
  const { dashboardId } = useRouteInfo();
  const dashboardQuery = useDashboard(dashboardId);
  const name = dashboardQuery.data?.dashboard?.name;
  return useMemo(
    () => (dashboardId == null ? NO_BREADCRUMB_LABELS : { dashboardId: name ?? "" }),
    [dashboardId, name],
  );
};
